import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAppData } from '../AppData';
import {
  DIMENSION_KINDS,
  DIMENSION_LABELS,
  DimensionKind,
  Dimension,
  PROYECTO_STATUSES,
  URGENCIAS,
} from '../domain/types';
import { fmtDia, toDateInput, errMsg } from '../domain/util';

export default function Dimensiones() {
  const { reload } = useAppData();
  const [kind, setKind] = useState<DimensionKind>('espacio');
  const [data, setData] = useState<Record<string, Dimension[]>>({});
  const [form, setForm] = useState<Record<string, any> | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setData(await api.listAllDimensions({ activeOnly: false }));
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  useEffect(() => { load(); }, []);

  const items = data[kind] || [];
  const newForm = () => setForm({ kind, name: '', code: '', active: true });

  const save = async () => {
    if (!form) return;
    setBusy(true); setErr('');
    try {
      const { kind: _k, ...payload } = form;
      await api.upsertDimension({ kind, payload });
      setForm(null);
      await load();
      await reload();
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const deactivate = async (id: string) => {
    if (!confirm('¿Desactivar este registro?')) return;
    try {
      await api.deactivateDimension({ kind, id });
      await load();
      await reload();
    } catch (e) { setErr(errMsg(e)); }
  };

  return (
    <div className="grid">
      <h1>Dimensiones</h1>
      <div className="row">
        {DIMENSION_KINDS.map((k) => (
          <span key={k} className={`chip ${kind === k ? 'active' : ''}`} style={{ cursor: 'pointer' }} onClick={() => { setKind(k); setForm(null); }}>
            {DIMENSION_LABELS[k]}
          </span>
        ))}
      </div>

      {err && <div className="error">{err}</div>}

      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>{DIMENSION_LABELS[kind]}</h2>
          <button className="primary" onClick={newForm}>+ Nuevo</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Nombre</th><th>Código</th>
              {kind === 'pieza' && <th>Categoría</th>}
              {kind === 'proyecto' && <><th>Dirección</th><th>Estado</th><th>Deadline</th></>}
              <th>Activo</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td>{d.name}</td><td>{d.code}</td>
                {kind === 'pieza' && <td>{d.categoria}</td>}
                {kind === 'proyecto' && <><td>{d.direccion_asociada}</td><td>{d.status}</td><td>{fmtDia(d.deadline)}</td></>}
                <td>{d.active ? 'Sí' : 'No'}</td>
                <td>
                  <button onClick={() => setForm({ kind, ...d, start_date: toDateInput(d.start_date), deadline: toDateInput(d.deadline), finished_date: toDateInput(d.finished_date) })}>Editar</button>{' '}
                  {d.active && <button className="danger" onClick={() => deactivate(d.id)}>Desactivar</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="muted">Sin registros.</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="panel grid" style={{ maxWidth: 720 }}>
          <h2>{form.id ? 'Editar' : 'Nuevo'} — {DIMENSION_LABELS[kind]}</h2>
          <div className="grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
            <div><label>Nombre *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label>Código</label><input value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div><label>Activo</label>
              <select value={String(form.active)} onChange={(e) => setForm({ ...form, active: e.target.value === 'true' })}>
                <option value="true">Sí</option><option value="false">No</option>
              </select>
            </div>
          </div>

          {kind === 'pieza' && (
            <div><label>Categoría *</label>
              <select value={form.categoria || ''} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                <option value="">—</option>
                {(data.categoria || []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          )}

          {kind === 'proyecto' && (
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div><label>Dirección *</label>
                <select value={form.direccion_asociada || ''} onChange={(e) => setForm({ ...form, direccion_asociada: e.target.value })}>
                  <option value="">—</option>
                  {(data.direccion || []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div><label>Estado</label>
                <select value={form.status || 'planning'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {PROYECTO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label>Urgencia</label>
                <select value={form.urgencia || ''} onChange={(e) => setForm({ ...form, urgencia: e.target.value })}>
                  <option value="">—</option>
                  {URGENCIAS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div><label>Inicio</label><input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><label>Deadline</label><input type="date" value={form.deadline || ''} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
              <div><label>Finalizado</label><input type="date" value={form.finished_date || ''} onChange={(e) => setForm({ ...form, finished_date: e.target.value })} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label>Descripción</label><textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
          )}

          <div className="row">
            <button onClick={() => setForm(null)}>Cancelar</button>
            <button className="primary" disabled={busy} onClick={save}>Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}
